import {jsx, ParamComponent} from 'panel';

import {IronContextRegistry} from 'common/report/context-registry';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {BannerEl} from 'common/components/banner';
import {BannerAction, BannerSize, BannerTheme, BannerType} from 'common/components/banner/types';
import {CustomAlertsCountResults} from 'app_api/projects/alerts/custom/types';
import {formatPercent} from 'common/util';

type AlertLimitBannerParams = {
  showManageButton?: boolean;
  showApproachingLimit?: boolean;
};

// Currently only anomaly detection limits are implemented. When regular alert limits are agreed upon this banner
// and the alertLimits context should be expanded upon to handle both alert type limits.
export class AlertLimitsBanner extends ParamComponent<AlertLimitBannerParams, EmptyObj, IronContextRegistry> {
  get config() {
    return {
      contexts: [`alertLimits` as const],
      params: {
        showManageButton: Boolean,
        showApproachingLimit: Boolean,
      },
      defaultParams: {
        showManageButton: false,
        showApproachingLimit: false,
      },
      template: () => {
        return (
          <div>
            {this.shouldShowBanner() ? (
              <BannerEl
                sel={`.alert-limit-banner`}
                attrs={{
                  theme:
                    this.params.showApproachingLimit && this.isApproachingAlertLimit()
                      ? BannerTheme.Info
                      : BannerTheme.Warn,
                  type: BannerType.Embedded,
                  size: BannerSize.ExtraSmall,
                  'header-text': this.getHeaderText(),
                  'body-text': this.getBodyText(),
                  'footer-text': this.getFooterText(),
                  ctas: this.getCTAs(),
                  'style-override': !this.params.showApproachingLimit
                    ? `.banner-wrapper .banner-panel .banner-copy {margin-bottom: 3px;}`
                    : null,
                  'banner-tracking-type': `Custom Alerts Limit`,
                }}
              />
            ) : null}
          </div>
        );
      },
    };
  }

  isApproachingAlertLimit() {
    return this.isBelowLimit() && this.alertCount() >= this.alertLimit() - 10;
  }

  shouldShowBanner() {
    return !this.isBelowLimit() || (this.params.showApproachingLimit && this.isApproachingAlertLimit());
  }

  getCTAs() {
    const ctas = [];
    if (this.params.showManageButton) {
      ctas.push({
        actionName: BannerAction.Manage,
        text: `Manage Alerts`,
        href: `/settings/#account/alert-settings`,
      });
    }
    ctas.push({
      actionName: BannerAction.Help,
      text: `Why do we limit alerts?`,
      href: `https://docs.mixpanel.com/docs/analysis/advanced/alerts#overview`,
    });
    return ctas;
  }

  getFooterText() {
    let footerText = ``;
    if (this.params.showApproachingLimit && this.shouldShowBanner()) {
      const alertCount = this.alertCount();
      const alertLimit = this.alertLimit();
      if (alertCount && alertLimit) {
        const alertPercentage = formatPercent(alertCount / alertLimit, 0);
        footerText = `${alertCount}/${alertLimit} alerts used. ${alertPercentage} capacity.`;
      }
    }
    return footerText;
  }

  getBodyText() {
    let bodyText = ``;
    const numOfAlerts = this.alertCount();
    const alertLimit = this.alertLimit();
    if (numOfAlerts >= alertLimit) {
      bodyText = `Your organization has reached the limit for active anomaly detection alerts.
      Delete old anomaly detection alerts to make space for new ones.`;
    } else if (this.params.showApproachingLimit && this.isApproachingAlertLimit()) {
      bodyText = `Your organization is approaching the limit for active anomaly detection alerts.
      Delete old anomaly detection alerts to make space for new ones.`;
    }
    return bodyText;
  }

  getHeaderText() {
    let headerText = ``;
    if (!this.isBelowLimit()) {
      if (this.params.showApproachingLimit) {
        headerText = `You've reached the anomaly detection alert limit.`;
      } else {
        headerText = `Alert cannot be created with anomaly detection criteria`;
      }
    }
    return headerText;
  }

  getAlertLimitContext(): CustomAlertsCountResults {
    return this.getContext(`alertLimits`).get().value ?? null;
  }

  alertLimit() {
    return this.getAlertLimitContext()?.alert_limit;
  }

  alertCount() {
    return this.getAlertLimitContext()?.anomaly_alerts_count;
  }

  isBelowLimit() {
    return this.getAlertLimitContext()?.is_below_limit ?? true;
  }
}

export const AlertLimitsBannerEl = defineAndMakeJsxFactory<any>(`alert-limits-banner`, AlertLimitsBanner);
